import { inject, Injectable } from '@angular/core';
import {
    BehaviorSubject,
    combineLatest,
    filter,
    forkJoin,
    map,
    Observable,
    of,
    switchMap,
    takeUntil,
    tap,
} from 'rxjs';
import {
    LogicStepDefinition,
    LogicStepParameter,
    LogicStepType,
    ProcessLogicStep,
    SystemEntity,
    WdxDestroyClass,
    WorkflowConfiguration,
    WorkflowUpdate,
} from '@wdx/shared/utils';
import { WorkflowApiService } from '@wdx/clmi/api-services/services';
import {
    NodeAddedEvent,
    WorkflowContext,
    WorkflowLogicStepDef,
    WorkflowNode,
    WorkflowNodeType,
} from '../models';
import { WorkflowNodeHelperService } from './workflow-node-helper.service';
import * as R from 'ramda';
import { v4 as uuidv4 } from 'uuid';
import { WorkflowDesignerConfig } from '../models/workflow-designer-config';
import { WorkflowQueryNodeHelper } from './workflow-query-node-helper.service';
import { LogicstepCodeParametersCachedApiService } from './logicstep-code-parameters-cached-api.service';
import { LogicstepApiService } from '@wdx/clmi/clmi-swagger-gen/api-services';

@Injectable({ providedIn: 'root' })
export class WorkflowDesignerFacade extends WdxDestroyClass {
    private logicStepApiService = inject(LogicstepApiService);
    private logicStepParameterCachedApiService = inject(
        LogicstepCodeParametersCachedApiService,
    );
    private workflowApiService = inject(WorkflowApiService);

    private nodeHelper = inject(WorkflowNodeHelperService);
    private queryNodeHelper = inject(WorkflowQueryNodeHelper);

    private selectedNode = new BehaviorSubject<WorkflowNode | null>(null);
    private parameters = new BehaviorSubject<LogicStepParameter[] | null>(null);
    private paramValues = new BehaviorSubject<Record<string, any> | null>(null);
    private workflowNodes = new BehaviorSubject<WorkflowNode[]>([]);
    private stepDefinitions = new BehaviorSubject<WorkflowLogicStepDef[]>([]);
    private initialStepDefinitions: WorkflowLogicStepDef[] = [];
    private stepDefinitionsByEntityType = new Map<
        SystemEntity,
        WorkflowLogicStepDef[]
    >();
    private showingStepSelection = new BehaviorSubject<boolean>(false);

    public config = new BehaviorSubject<WorkflowDesignerConfig | null>(null);
    public readonly workflowContext$: Observable<WorkflowContext> =
        combineLatest([
            this.selectedNode,
            this.parameters,
            this.paramValues,
        ]).pipe(
            map(([node, parameters, paramValues]) => ({
                node,
                parameters,
                paramValues,
            })),
        );

    public readonly workflowNodes$ = this.workflowNodes.asObservable();
    public readonly showingStepSelection$ =
        this.showingStepSelection.asObservable();

    private readonly availableStepDefinitions$: Observable<
        WorkflowLogicStepDef[]
    > = combineLatest([this.stepDefinitions, this.workflowNodes]).pipe(
        map(([stepDefs, nodes]) => {
            const policy = this.queryNodeHelper.getQueryPolicy();
            const hasQuery = nodes.some((n) => n.nodeType === 'query');

            return stepDefs
                .map((stepDef) => {
                    if (stepDef.type !== ('Query' as LogicStepType)) {
                        return stepDef;
                    }

                    if (!policy.allowed) {
                        return null;
                    }

                    return {
                        ...stepDef,
                        isDisabled: policy.required || hasQuery,
                    };
                })
                .filter(Boolean);
        }),
    );

    groupedStepDefinitions$ = this.availableStepDefinitions$.pipe(
        map(
            R.pipe(
                R.groupBy((stepDef: LogicStepDefinition) => stepDef.type),
                R.toPairs,
                R.map(([type, steps]) => ({ type, steps })),
            ),
        ),
    );

    setConfig(config: WorkflowDesignerConfig) {
        this.config.next(config);
        this.queryNodeHelper.setConfig(config);
    }

    loadDefinitions() {
        const workflowConfig = this.config.value.configuration;
        const targetEntityType = this.config.value.targetEntityType;
        const workflowLogicSteps = workflowConfig?.logicSteps ?? [];
        const workflowStepCodes = this.getWorkflowStepCodes(workflowLogicSteps);
        this.stepDefinitionsByEntityType.clear();
        this.workflowNodes.next([]);

        this.preloadWorkflowStepDefinitions(
            targetEntityType,
            workflowLogicSteps,
        )
            .pipe(
                takeUntil(this.destroyed$),
                switchMap((stepDefs) => {
                    const loadedCodes = stepDefs
                        .map((stepDef) => stepDef?.code)
                        .filter(Boolean);
                    const missingCodes = workflowStepCodes.filter(
                        (code) => !loadedCodes.includes(code),
                    );
                    return (
                        missingCodes.length
                            ? this.logicStepApiService.get().pipe(
                                  map((allStepDefs) =>
                                      allStepDefs.filter(
                                          (stepDef) =>
                                              !!stepDef?.code &&
                                              missingCodes.includes(
                                                  stepDef.code,
                                              ),
                                      ),
                                  ),
                              )
                            : of([])
                    ).pipe(
                        map((missingStepDefs) => ({
                            stepDefs,
                            missingStepDefs,
                        })),
                    );
                }),
                tap(({ stepDefs, missingStepDefs }) => {
                    this.applyStepDefinitions(stepDefs, true);
                    this.nodeHelper.stepDefinitions =
                        this.mergeStepDefinitionsByCode([
                            ...stepDefs,
                            ...missingStepDefs,
                        ]);
                }),
                switchMap(() => {
                    const stepNodeObservables = workflowLogicSteps.map(
                        (logicStep) =>
                            this.nodeHelper.convertStepToNode(logicStep),
                    );

                    return stepNodeObservables.length > 0
                        ? forkJoin(stepNodeObservables).pipe(
                              map((nodes) =>
                                  nodes.filter(
                                      (node): node is WorkflowNode => !!node,
                                  ),
                              ),
                          )
                        : of<WorkflowNode[]>([]);
                }),
                map((nodes) => {
                    return this.queryNodeHelper.applyInitialQueryNode(nodes);
                }),
            )
            .subscribe((nodes) => {
                if (!nodes || nodes.length === 0) {
                    return;
                }
                this.workflowNodes.next(nodes);
            });
    }

    startStepSelection(nodeAdded?: NodeAddedEvent) {
        const isSystemEntity = (
            targetEntityType: SystemEntity | null,
        ): targetEntityType is SystemEntity => !!targetEntityType;

        this.clear();
        this.showingStepSelection.next(true);
        if (!nodeAdded?.logicCode) {
            if (this.initialStepDefinitions.length) {
                this.stepDefinitions.next([...this.initialStepDefinitions]);
                this.nodeHelper.stepDefinitions = [
                    ...this.initialStepDefinitions,
                ];
            }
            return;
        }

        this.resolveSelectionEntityType(nodeAdded)
            .pipe(
                takeUntil(this.destroyed$),
                filter(isSystemEntity),
                switchMap((targetEntityType) => {
                    const cachedStepDefs =
                        this.stepDefinitionsByEntityType.get(targetEntityType);

                    if (cachedStepDefs?.length) {
                        return of(cachedStepDefs);
                    }

                    return this.logicStepApiService
                        .get(targetEntityType)
                        .pipe(
                            tap((stepDefs) =>
                                this.stepDefinitionsByEntityType.set(
                                    targetEntityType,
                                    [...stepDefs],
                                ),
                            ),
                        );
                }),
            )
            .subscribe((stepDefs) => this.applyStepDefinitions(stepDefs));
    }

    selectNode(node: WorkflowNode) {
        this.selectedNode.next(node);
        switch (node.nodeType) {
            case WorkflowNodeType.LogicStep:
                this.logicStepParameterCachedApiService
                    .get(node.code)
                    .pipe(takeUntil(this.destroyed$))
                    .subscribe((params) => this.parameters.next(params));
                break;
            case WorkflowNodeType.Query:
            default:
                this.parameters.next(null);
                break;
        }
        this.paramValues.next(node.payload);
    }

    startAddNode(
        stepDef: LogicStepDefinition,
        nodeAdded: NodeAddedEvent,
    ): string {
        const nodeId = uuidv4();
        this.paramValues.next({});
        this.showingStepSelection.next(false);

        if (stepDef.type !== ('Query' as LogicStepType)) {
            this.logicStepParameterCachedApiService
                .get(stepDef.code)
                .pipe(takeUntil(this.destroyed$))
                .subscribe((params) => {
                    this.parameters.next(params);
                    this.initNode(nodeId, stepDef, nodeAdded);
                });
        } else {
            this.initQueryNode(nodeId);
        }

        return this.nodeHelper.buildActiveNodeId(nodeId, nodeAdded.logicCode);
    }

    removeNode(node: WorkflowNode) {
        const currentNodes = this.workflowNodes.value;
        const updatedNodes = this.nodeHelper.removeNodeRecursively(
            currentNodes,
            node.payload.Id,
        );

        this.workflowNodes.next(updatedNodes);

        if (this.selectedNode.value?.payload.Id === node.payload.Id) {
            this.clear();
        }
    }

    updateNode(paramValues: Record<string, any>, isValid: boolean) {
        const node = this.selectedNode.value;
        if (!node) {
            return;
        }

        const updatedNode: WorkflowNode = {
            ...node,
            isValid: isValid,
            payload: {
                ...node.payload,
                ...paramValues,
            },
        };

        const nodes = this.nodeHelper.updateNodeRecursively(
            this.workflowNodes.value,
            updatedNode.payload.Id,
            updatedNode,
        );
        this.workflowNodes.next(nodes);
    }

    allNodesValid(): boolean {
        return !this.hasInvalidNodes(this.workflowNodes.value);
    }

    saveWorkflow(): Observable<WorkflowConfiguration> {
        const output = this.nodeHelper.getWorkflowOutput(
            this.workflowNodes.value,
        );
        const config = this.config.value;
        return this.workflowApiService
            .update(config.entityId, {
                code: config.code,
                name: config.name,
                configuration: output,
            } as WorkflowUpdate)
            .pipe(map(() => output));
    }

    clear() {
        this.selectedNode.next(null);
        this.parameters.next(null);
        this.paramValues.next(null);
        this.showingStepSelection.next(false);
    }

    resolveEntityTypeForNode$(
        node: WorkflowNode | null,
    ): Observable<SystemEntity> {
        const fallbackEntityType = this.config.value.targetEntityType;

        if (!node?.payload?.Id || !node.logicCode) {
            return of(fallbackEntityType);
        }

        return this.resolveSelectionEntityType({
            parentId: node.payload.Id,
            logicCode: node.logicCode,
            isNewLogic: false,
        }).pipe(map((entityType) => entityType ?? fallbackEntityType));
    }

    private hasInvalidNodes(nodes: WorkflowNode[]): boolean {
        return nodes.some((node) => {
            if (!node.isValid) {
                return true;
            }
            const children = Object.values(node.children || {}).flat();
            return this.hasInvalidNodes(children);
        });
    }

    private initNode(
        nodeId: string,
        stepDef: LogicStepDefinition,
        nodeAdded: NodeAddedEvent,
    ) {
        if (!nodeAdded?.parentId) {
            return;
        }
        const params = this.parameters.value;
        const newNode = this.nodeHelper.buildNode(nodeId, {}, stepDef, params);
        newNode.logicCode = nodeAdded.logicCode;

        this.selectedNode.next(newNode);

        const steps = this.nodeHelper.createNode(
            [...this.workflowNodes.value],
            newNode,
            nodeAdded,
        );
        this.workflowNodes.next(steps);
    }

    private initQueryNode(nodeId: string) {
        const currentNodes = this.workflowNodes.value;

        const queryNode = this.queryNodeHelper.buildQueryNode(nodeId);

        this.selectedNode.next(queryNode);
        this.workflowNodes.next([queryNode, ...currentNodes]);
    }

    private applyStepDefinitions(
        stepDefs: WorkflowLogicStepDef[],
        setAsInitial = false,
    ) {
        const policy = this.queryNodeHelper.getQueryPolicy();

        if (policy.allowed || policy.required) {
            stepDefs = [this.queryNodeHelper.queryStepDefinition, ...stepDefs];
        }

        this.stepDefinitions.next(stepDefs);
        this.nodeHelper.stepDefinitions = stepDefs;
        if (setAsInitial) {
            this.initialStepDefinitions = [...stepDefs];
        }
    }

    private preloadWorkflowStepDefinitions(
        targetEntityType: SystemEntity,
        logicSteps: ProcessLogicStep[],
    ): Observable<WorkflowLogicStepDef[]> {
        const stepCodes = this.getWorkflowStepCodes(logicSteps);

        return this.getStepDefinitionsForEntity(targetEntityType).pipe(
            switchMap((baseStepDefs) =>
                this.getAdditionalEntityTypes(targetEntityType, stepCodes).pipe(
                    switchMap((additionalEntityTypes) =>
                        additionalEntityTypes.length
                            ? forkJoin(
                                  additionalEntityTypes.map((entityType) =>
                                      this.getStepDefinitionsForEntity(
                                          entityType,
                                      ),
                                  ),
                              ).pipe(
                                  map((additionalDefSets) => [
                                      ...baseStepDefs,
                                      ...additionalDefSets.flat(),
                                  ]),
                              )
                            : of(baseStepDefs),
                    ),
                ),
            ),
            map((allDefinitions) =>
                this.mergeStepDefinitionsByCode(allDefinitions),
            ),
        );
    }

    private getStepDefinitionsForEntity(
        entityType: SystemEntity,
    ): Observable<WorkflowLogicStepDef[]> {
        const cached = this.stepDefinitionsByEntityType.get(entityType);
        if (cached?.length) {
            return of(cached);
        }

        return this.logicStepApiService
            .get(entityType)
            .pipe(
                tap((stepDefs) =>
                    this.stepDefinitionsByEntityType.set(entityType, [
                        ...stepDefs,
                    ]),
                ),
            );
    }

    private getAdditionalEntityTypes(
        targetEntityType: SystemEntity,
        stepCodes: string[],
    ): Observable<SystemEntity[]> {
        if (!stepCodes.length) {
            return of([]);
        }

        return forkJoin(
            stepCodes.map((code) =>
                this.logicStepParameterCachedApiService.get(code),
            ),
        ).pipe(
            map((parameterSets) =>
                Array.from(
                    new Set(
                        parameterSets
                            .flatMap(
                                (parameter: any) =>
                                    parameter?.applicableEntityTypes || [],
                            )
                            .filter(Boolean),
                    ),
                )
                    .filter((entityType) => entityType !== targetEntityType)
                    .map((entityType) => entityType as SystemEntity),
            ),
        );
    }

    private mergeStepDefinitionsByCode(
        definitions: WorkflowLogicStepDef[],
    ): WorkflowLogicStepDef[] {
        const byCode = new Map<string, WorkflowLogicStepDef>();
        definitions.forEach((stepDef) => {
            if (stepDef?.code && !byCode.has(stepDef.code)) {
                byCode.set(stepDef.code, stepDef);
            }
        });
        return Array.from(byCode.values());
    }

    private getWorkflowStepCodes(
        logicSteps: ProcessLogicStep[] = [],
    ): string[] {
        const stepCodes = new Set<string>();
        const getCode = (value: unknown): string | undefined => {
            if (!value || typeof value !== 'object') {
                return undefined;
            }

            const code =
                'Code' in value
                    ? value.Code
                    : 'code' in value
                      ? value.code
                      : undefined;

            if (typeof code === 'string') {
                return code;
            }

            return undefined;
        };

        const visit = (logicStep: ProcessLogicStep): void => {
            const code = getCode(logicStep);
            if (code) {
                stepCodes.add(code);
            }

            Object.values(logicStep)
                .filter(Array.isArray)
                .flat()
                .forEach((item) => {
                    if (getCode(item)) {
                        visit(item);
                    }
                });
        };

        logicSteps.forEach(visit);

        return Array.from(stepCodes);
    }

    private resolveSelectionEntityType(
        nodeAdded?: NodeAddedEvent,
    ): Observable<SystemEntity | null> {
        if (!nodeAdded?.logicCode) {
            return of(null);
        }

        const contextNode = this.findLogicContextNode(nodeAdded);
        if (!contextNode?.code) {
            return of(null);
        }

        const parentEntityType$ = contextNode.logicCode
            ? this.resolveSelectionEntityType({
                  parentId: contextNode.payload.Id,
                  logicCode: contextNode.logicCode,
                  isNewLogic: false,
              })
            : of(this.config.value.targetEntityType);

        return parentEntityType$.pipe(
            switchMap((parentEntityType) =>
                this.resolveChildEntityType$(
                    contextNode.code,
                    nodeAdded.logicCode,
                    parentEntityType ?? this.config.value.targetEntityType,
                ),
            ),
        );
    }

    private resolveChildEntityType$(
        stepCode: string,
        logicCode: string,
        fallbackEntityType: SystemEntity,
    ): Observable<SystemEntity> {
        return this.logicStepParameterCachedApiService
            .get(stepCode)
            .pipe(
                map(
                    (params) =>
                        (params.find((param) => param?.code === logicCode)
                            ?.applicableEntityTypes?.[0] as SystemEntity) ??
                        fallbackEntityType,
                ),
            );
    }

    private findLogicContextNode(
        nodeAdded: NodeAddedEvent,
    ): WorkflowNode | null {
        const parentId = nodeAdded?.parentId;

        if (!parentId || parentId === 'root') {
            return null;
        }

        const matchesContextNode = (node: WorkflowNode): boolean => {
            if (nodeAdded.isNewLogic) {
                return node.payload?.Id === parentId;
            }

            if (!nodeAdded.logicCode) {
                return false;
            }

            return (node.children?.[nodeAdded.logicCode] ?? []).some(
                (child) => child.payload?.Id === parentId,
            );
        };

        const findInNodes = (nodes: WorkflowNode[]): WorkflowNode | null =>
            nodes.find(matchesContextNode) ??
            nodes
                .map((node) =>
                    findInNodes(Object.values(node.children ?? {}).flat()),
                )
                .find((node): node is WorkflowNode => !!node) ??
            null;

        return findInNodes(this.workflowNodes.value);
    }
}
